/**
 * Funnel tab — acquisition + activation funnel.
 *
 * Layout:
 *   [ Funnel visualization | Channel breakdown table ]
 *   [ Phase 2 notice about cross-channel mobile install attribution ]
 */
import React from 'react';
import { Info } from 'lucide-react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type {
  AttributionChannelsPayload,
  FunnelPayload,
  OnboardingFunnelPayload,
} from '../types';
import { AcquisitionFunnel } from '../components/AcquisitionFunnel';
import { OnboardingStageFunnel } from '../components/OnboardingStageFunnel';
import { ChannelBreakdownTable } from '../components/ChannelBreakdownTable';
import { ChartCard } from '../components/ChartCard';

export interface FunnelTabProps {
  range: AnalyticsRange;
}

export const FunnelTab: React.FC<FunnelTabProps> = ({ range }) => {
  const funnel = useAnalyticsQuery<FunnelPayload>(
    ['funnel', range.queryString],
    `/api/admin/analytics/funnel?${range.queryString}`,
  );
  const channels = useAnalyticsQuery<AttributionChannelsPayload>(
    ['funnel-channels', range.queryString],
    `/api/admin/analytics/attribution/channels?${range.queryString}`,
  );
  // The acquisition funnel above steps signup -> onboarding_complete in ONE
  // hop. This opens that hop, which is where the largest measured loss sits.
  const onboarding = useAnalyticsQuery<OnboardingFunnelPayload>(
    ['funnel-onboarding', range.queryString],
    `/api/admin/analytics/funnel/onboarding?${range.queryString}`,
  );

  return (
    <div className="space-y-6">
      <ChartCard
        title="Inside onboarding"
        subtitle="The span the acquisition funnel collapses into one hop: signup through first goal, stage by stage. Same signup-cohort semantics."
        loading={onboarding.isLoading}
        error={onboarding.error?.message ?? null}
        isEmpty={!onboarding.data?.data.steps?.length}
        onRetry={() => onboarding.refetch()}
      >
        <OnboardingStageFunnel steps={onboarding.data?.data.steps ?? []} />
      </ChartCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard
          title="Acquisition funnel"
          subtitle="Signup-cohort funnel: of users who signed up in the selected range, how many reached each stage since. Conversion stages match RevenueCat."
          loading={funnel.isLoading}
          error={funnel.error?.message ?? null}
          isEmpty={!funnel.data?.data.stages?.length}
          onRetry={() => funnel.refetch()}
          className="lg:col-span-2"
        >
          <AcquisitionFunnel stages={funnel.data?.data.stages ?? []} />
        </ChartCard>

        <ChartCard
          title="Channel mix"
          subtitle="Signups by resolved attribution channel"
          loading={channels.isLoading}
          error={channels.error?.message ?? null}
          isEmpty={!channels.data?.data.channels?.length}
          onRetry={() => channels.refetch()}
          className="lg:col-span-1"
        >
          <ChannelBreakdownTable
            rows={channels.data?.data.channels ?? []}
            totalSignups={channels.data?.data.total_signups ?? 0}
          />
        </ChartCard>
      </div>

      {/* Phase-2 deferral notice */}
      <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50/60 p-3 text-[12px] text-blue-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" aria-hidden />
        <div>
          <p className="font-semibold">Mobile install attribution — Phase 2</p>
          <p className="mt-0.5 text-blue-800/80">
            Phase 1 captures web UTMs, Apple Search Ads + SKAdNetwork (iOS), and Play Install
            Referrer (Android). Cross-channel attribution from Meta/TikTok app-install campaigns
            requires an SDK integration (AppsFlyer or Adjust) and will land in Phase 2.
          </p>
        </div>
      </div>
    </div>
  );
};
